import React from 'react';
import { supabase } from './supabaseClient';

// Error types
export interface AppError {
  id?: string;
  type: 'network' | 'database' | 'validation' | 'authentication' | 'authorization' | 'unknown';
  message: string;
  details?: any;
  timestamp: string;
  userId?: string;
  component?: string;
  action?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved?: boolean;
}

export interface LoadingState {
  [key: string]: boolean;
}

export interface NotificationMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  timestamp: string;
  actionText?: string;
  actionUrl?: string;
  actionHandler?: () => void;
  dismissible?: boolean;
}

// Error handling service
export class ErrorService {
  private static errors: AppError[] = [];
  private static listeners: ((errors: AppError[]) => void)[] = [];

  // Add error listener
  static addListener(callback: (errors: AppError[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify listeners
  private static notifyListeners() {
    this.listeners.forEach(listener => listener([...this.errors]));
  }

  // Log error
  static logError(error: Omit<AppError, 'id' | 'timestamp'>): string {
    const errorId = this.generateErrorId();
    const appError: AppError = {
      ...error,
      id: errorId,
      timestamp: new Date().toISOString(),
    };

    this.errors.push(appError);
    this.notifyListeners();

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('App Error:', appError);
    }

    // Store in database for critical errors
    if (appError.severity === 'critical' || appError.severity === 'high') {
      this.storeErrorInDatabase(appError);
    }

    return errorId;
  }

  // Handle common error types
  static handleNetworkError(error: any, component?: string, action?: string): string {
    return this.logError({
      type: 'network',
      message: 'Network connection failed. Please check your internet connection.',
      details: error,
      component,
      action,
      severity: 'medium'
    });
  }

  static handleDatabaseError(error: any, component?: string, action?: string): string {
    return this.logError({
      type: 'database',
      message: 'Database operation failed. Please try again later.',
      details: error,
      component,
      action,
      severity: 'high'
    });
  }

  static handleValidationError(message: string, details?: any, component?: string): string {
    return this.logError({
      type: 'validation',
      message,
      details,
      component,
      severity: 'low'
    });
  }

  static handleAuthenticationError(message: string = 'Authentication failed', component?: string): string {
    return this.logError({
      type: 'authentication',
      message,
      component,
      severity: 'high'
    });
  }

  static handleAuthorizationError(message: string = 'Access denied', component?: string, action?: string): string {
    return this.logError({
      type: 'authorization',
      message,
      component,
      action,
      severity: 'medium'
    });
  }

  // Get errors
  static getErrors(): AppError[] {
    return [...this.errors];
  }

  static getErrorsByType(type: AppError['type']): AppError[] {
    return this.errors.filter(error => error.type === type);
  }

  static getUnresolvedErrors(): AppError[] {
    return this.errors.filter(error => !error.resolved);
  }

  // Mark error as resolved
  static resolveError(errorId: string): boolean {
    const errorIndex = this.errors.findIndex(error => error.id === errorId);
    if (errorIndex !== -1) {
      this.errors[errorIndex].resolved = true;
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // Clear errors
  static clearErrors(): void {
    this.errors = [];
    this.notifyListeners();
  }

  static clearResolvedErrors(): void {
    this.errors = this.errors.filter(error => !error.resolved);
    this.notifyListeners();
  }

  // Store error in database
  private static async storeErrorInDatabase(error: AppError): Promise<void> {
    try {
      await supabase
        .from('error_logs')
        .insert([{
          error_type: error.type,
          message: error.message,
          details: error.details,
          user_id: error.userId,
          component: error.component,
          action: error.action,
          severity: error.severity,
          timestamp: error.timestamp
        }]);
    } catch (dbError) {
      console.error('Failed to store error in database:', dbError);
    }
  }

  // Generate unique error ID
  private static generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get error statistics
  static getErrorStatistics(): {
    total: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
    resolved: number;
    unresolved: number;
  } {
    const total = this.errors.length;
    const resolved = this.errors.filter(e => e.resolved).length;
    const unresolved = total - resolved;

    const byType = this.errors.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const bySeverity = this.errors.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      byType,
      bySeverity,
      resolved,
      unresolved
    };
  }
}

// Loading state management
export class LoadingService {
  private static loadingStates: LoadingState = {};
  private static listeners: ((states: LoadingState) => void)[] = [];

  // Add listener
  static addListener(callback: (states: LoadingState) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Subscribe to loading states (alias for addListener)
  static subscribe(callback: (states: LoadingState) => void): () => void {
    return this.addListener(callback);
  }

  // Notify listeners
  private static notifyListeners() {
    this.listeners.forEach(listener => listener({ ...this.loadingStates }));
  }

  // Set loading state
  static setLoading(key: string, loading: boolean): void {
    this.loadingStates[key] = loading;
    this.notifyListeners();
  }

  // Get loading state
  static isLoading(key: string): boolean {
    return this.loadingStates[key] || false;
  }

  // Get all loading states
  static getAllLoadingStates(): LoadingState {
    return { ...this.loadingStates };
  }

  // Check if any operation is loading
  static isAnyLoading(): boolean {
    return Object.values(this.loadingStates).some(loading => loading);
  }

  // Clear all loading states
  static clearAllLoading(): void {
    this.loadingStates = {};
    this.notifyListeners();
  }

  // Async operation wrapper
  static async withLoading<T>(
    key: string,
    operation: () => Promise<T>,
    errorHandler?: (error: any) => void
  ): Promise<T | null> {
    this.setLoading(key, true);
    try {
      const result = await operation();
      return result;
    } catch (error) {
      if (errorHandler) {
        errorHandler(error);
      } else {
        ErrorService.logError({
          type: 'unknown',
          message: 'Operation failed',
          details: error,
          action: key,
          severity: 'medium'
        });
      }
      return null;
    } finally {
      this.setLoading(key, false);
    }
  }
}

// Notification service
export class NotificationService {
  private static notifications: NotificationMessage[] = [];
  private static listeners: ((notifications: NotificationMessage[]) => void)[] = [];
  private static subscribers: ((notification: Omit<NotificationMessage, 'id' | 'timestamp'>) => void)[] = [];

  // Add listener
  static addListener(callback: (notifications: NotificationMessage[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Subscribe to notifications
  static subscribe(callback: (notification: Omit<NotificationMessage, 'id' | 'timestamp'>) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(subscriber => subscriber !== callback);
    };
  }

  // Notify listeners
  private static notifyListeners() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  // Notify subscribers
  private static notifySubscribers(notification: Omit<NotificationMessage, 'id' | 'timestamp'>) {
    this.subscribers.forEach(subscriber => subscriber(notification));
  }

  // Show notification
  static show(notification: Omit<NotificationMessage, 'id' | 'timestamp'>): string {
    const id = this.generateNotificationId();
    const message: NotificationMessage = {
      ...notification,
      id,
      timestamp: new Date().toISOString(),
      duration: notification.duration || 5000
    };

    this.notifications.push(message);
    this.notifyListeners();
    this.notifySubscribers(notification);

    // Auto-remove non-persistent notifications
    if (!message.persistent && message.duration) {
      setTimeout(() => {
        this.remove(id);
      }, message.duration);
    }

    return id;
  }

  // Convenience methods
  static success(title: string, message: string, duration?: number): string {
    return this.show({ type: 'success', title, message, duration });
  }

  static error(title: string, message: string, persistent?: boolean): string {
    return this.show({ type: 'error', title, message, persistent });
  }

  static warning(title: string, message: string, duration?: number): string {
    return this.show({ type: 'warning', title, message, duration });
  }

  static info(title: string, message: string, duration?: number): string {
    return this.show({ type: 'info', title, message, duration });
  }

  // Remove notification
  static remove(id: string): boolean {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      this.notifications.splice(index, 1);
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // Clear all notifications
  static clear(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  // Get notifications
  static getAll(): NotificationMessage[] {
    return [...this.notifications];
  }

  static getByType(type: NotificationMessage['type']): NotificationMessage[] {
    return this.notifications.filter(n => n.type === type);
  }

  // Generate unique notification ID
  private static generateNotificationId(): string {
    return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Utility functions for common error scenarios
export const handleAsyncOperation = async <T>(
  operation: () => Promise<T>,
  options: {
    loadingKey?: string;
    errorMessage?: string;
    successMessage?: string;
    component?: string;
    action?: string;
  } = {}
): Promise<T | null> => {
  const { loadingKey, errorMessage, successMessage, component, action } = options;

  if (loadingKey) {
    LoadingService.setLoading(loadingKey, true);
  }

  try {
    const result = await operation();
    
    if (successMessage) {
      NotificationService.success('Success', successMessage);
    }
    
    return result;
  } catch (error) {
    const message = errorMessage || 'Operation failed. Please try again.';
    
    ErrorService.logError({
      type: 'unknown',
      message,
      details: error,
      component,
      action,
      severity: 'medium'
    });
    
    NotificationService.error('Error', message);
    return null;
  } finally {
    if (loadingKey) {
      LoadingService.setLoading(loadingKey, false);
    }
  }
};

// React hooks for services (to be used in components)
export const useErrorService = () => {
  const [errors, setErrors] = React.useState<AppError[]>(ErrorService.getErrors());

  React.useEffect(() => {
    const unsubscribe = ErrorService.addListener(setErrors);
    return unsubscribe;
  }, []);

  return {
    errors,
    logError: ErrorService.logError,
    resolveError: ErrorService.resolveError,
    clearErrors: ErrorService.clearErrors,
    getUnresolvedErrors: ErrorService.getUnresolvedErrors,
    getErrorStatistics: ErrorService.getErrorStatistics
  };
};

export const useLoadingService = () => {
  const [loadingStates, setLoadingStates] = React.useState<LoadingState>(LoadingService.getAllLoadingStates());

  React.useEffect(() => {
    const unsubscribe = LoadingService.addListener(setLoadingStates);
    return unsubscribe;
  }, []);

  return {
    loadingStates,
    setLoading: LoadingService.setLoading,
    isLoading: LoadingService.isLoading,
    isAnyLoading: LoadingService.isAnyLoading,
    withLoading: LoadingService.withLoading
  };
};

export const useNotificationService = () => {
  const [notifications, setNotifications] = React.useState<NotificationMessage[]>(NotificationService.getAll());

  React.useEffect(() => {
    const unsubscribe = NotificationService.addListener(setNotifications);
    return unsubscribe;
  }, []);

  return {
    notifications,
    show: NotificationService.show,
    success: NotificationService.success,
    error: NotificationService.error,
    warning: NotificationService.warning,
    info: NotificationService.info,
    remove: NotificationService.remove,
    clear: NotificationService.clear
  };
};